import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, PlayCircle, Eye } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  createPayrollRun,
  getPayrollRuns,
  getPeriods,
  processPayrollRun,
} from "../payrollService";
import PayrollTableEmpty from "../components/PayrollTableEmpty";

export default function PayrollRuns() {
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(true);

  const [form, setForm] = useState({
    period_id: "",
    run_name: "",
    run_type: "regular",
    notes: "",
  });

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const [runsData, periodsData] = await Promise.all([
        getPayrollRuns(companyId),
        getPeriods(companyId),
      ]);

      setRows(runsData || []);
      setPeriods(periodsData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load payroll runs");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await createPayrollRun({
        company_id: companyId,
        period_id: form.period_id,
        run_name: form.run_name,
        run_type: form.run_type,
        notes: form.notes,
      });
      setForm({
        period_id: "",
        run_name: "",
        run_type: "regular",
        notes: "",
      });
      load();
    } catch (err) {
      setMsg(err.message || "Failed to create payroll run");
    }
  }

  async function handleProcess(row) {
    const ok = window.confirm(`Process payroll run "${row.run_name}" now?`);
    if (!ok) return;
    try {
      setMsg("Processing payroll run...");
      await processPayrollRun(companyId, row.id);
      setMsg("Payroll run processed successfully.");
      load();
    } catch (err) {
      setMsg(err.message || "Failed to process payroll run");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-card">
        <div className="payroll-card-header">
          <h3>Create Payroll Run</h3>
        </div>

        <form className="payroll-form" onSubmit={handleCreate}>
          <div className="payroll-grid payroll-grid-4">
            <label>
              Period
              <select
                value={form.period_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, period_id: e.target.value }))
                }
                required
              >
                <option value="">Select period</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.period_name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Run Name
              <input
                value={form.run_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, run_name: e.target.value }))
                }
                required
              />
            </label>

            <label>
              Run Type
              <select
                value={form.run_type}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, run_type: e.target.value }))
                }
              >
                <option value="regular">Regular</option>
                <option value="off_cycle">Off Cycle</option>
                <option value="bonus">Bonus</option>
                <option value="adjustment">Adjustment</option>
                <option value="final_settlement">Final Settlement</option>
              </select>
            </label>

            <label>
              Notes
              <input
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </label>
          </div>

          <button className="primary-btn" type="submit">
            <Plus size={16} />
            Create Run
          </button>
        </form>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <h3>Payroll Runs</h3>
        </div>

        {!rows.length && !busy ? (
          <PayrollTableEmpty title="No payroll runs yet" />
        ) : (
          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Run Name</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Employees</th>
                  <th>Gross</th>
                  <th>Deductions</th>
                  <th>Net</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const period = Array.isArray(row.payroll_periods)
                    ? row.payroll_periods[0]
                    : row.payroll_periods;

                  return (
                    <tr key={row.id}>
                      <td>{row.run_name}</td>
                      <td>{period?.period_name || "-"}</td>
                      <td>
                        <span className={`pill ${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.employee_count || 0}</td>
                      <td>{Number(row.gross_total || 0).toLocaleString()}</td>
                      <td>
                        {Number(row.deduction_total || 0).toLocaleString()}
                      </td>
                      <td>{Number(row.net_total || 0).toLocaleString()}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => navigate(`/payroll/runs/${row.id}`)}
                          >
                            <Eye size={16} />
                          </button>
                          {row.status === "draft" ? (
                            <button
                              type="button"
                              className="icon-btn success"
                              onClick={() => handleProcess(row)}
                            >
                              <PlayCircle size={16} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {busy && (
                  <tr>
                    <td colSpan="8">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
